/**
 * Part C SP4 S3 — pure trust-score derivation for the Data-Trust Console.
 *
 * Extracted verbatim from the inline `deriveTrust` in `SiteAuditTab.tsx`
 * so the 0–100 trust score + four plain-language dimensions can be unit-
 * tested as a pure function (no React, no store) and re-tuned in one
 * place. The presentation layer (icons, CSS tone classes, the tour
 * anchors) stays in the component.
 *
 * ── ADDITIVE GUARANTEE ────────────────────────────────────────────────
 * The per-finding penalty is computed as
 *
 *     penalty = BASE_PENALTY_BY_SEVERITY[severity]
 *             × MULT_BY_TIER[tier]
 *             × criticalityFor(finding)
 *
 * and EVERY multiplier defaults to `1.0`:
 *
 *   - `BASE_PENALTY_BY_SEVERITY` reproduces the historic severity weights
 *     exactly (block 14 / warn 5 / info 1).
 *   - `MULT_BY_TIER` is `1.0` for every tier.
 *   - `CRITICALITY_BY_FACT` / `CRITICALITY_BY_CHECK` are EMPTY tables;
 *     `criticalityFor` returns `DEFAULT_CRITICALITY` (= 1.0) for every
 *     finding until someone adds a non-default entry.
 *
 * Therefore the extracted `deriveTrust` produces a BYTE-IDENTICAL score
 * and dimension breakdown to the prior inline implementation for any
 * input. The multiplier/criticality machinery is plumbing for a LATER
 * tuning step; it is intentionally a no-op in this slice.
 */
import type { UserAuditFinding, AuditSeverity, AuditTier, AuditCheckId } from '../../types/userAudit';
import type { CanonicalFactKey } from '../../types/auditCanonicalFacts';

/** The four plain-language trust dimensions the ledger surfaces. */
export type TrustDimensionId = 'conflicts' | 'provenance' | 'citation' | 'intent';

export interface TrustDimensionSummary {
  id: TrustDimensionId;
  name: string;
  sub: string;
  /** Worst severity present in this dimension. */
  tone: 'clean' | 'warn' | 'block';
  count: number;
  /** Mono micro-label for the count, e.g. "BLOCKING" / "TO REVIEW" / "ALIGNED". */
  note: string;
}

/** One line of the score's arithmetic — a severity bucket within a dimension. */
export interface TrustPenaltyLine {
  dimensionId: TrustDimensionId;
  dimensionName: string;
  severity: AuditSeverity;
  /** How many findings in this bucket. */
  count: number;
  /** Penalty charged per finding (base × tier × criticality). */
  each: number;
  /** count × each. */
  points: number;
}

/**
 * Full audit trail of how the 0–100 score was reached (2026-08-14).
 * The score used to be an unexplained number — "85/100" with no way to see
 * what cost the 15 points. Every consumer that shows the score must be able
 * to show this breakdown, so the number is checkable rather than trusted.
 */
export interface TrustBreakdown {
  /** Always 100 — the starting point. */
  startingScore: number;
  /** Σ points across all lines (before clamping). */
  totalPenalty: number;
  /** Non-empty penalty lines, worst-severity first then largest points. */
  lines: TrustPenaltyLine[];
  /** Findings excluded from the score because the user settled them. */
  excluded: { validated: number; dismissed: number };
  /** Penalty charged per severity — the published weights. */
  weights: Readonly<Record<AuditSeverity, number>>;
  /** The dimension carrying the most penalty, or null when nothing is charged. */
  worstDimension: { id: TrustDimensionId; name: string; points: number } | null;
}

export interface TrustResult {
  score: number;
  dimensions: TrustDimensionSummary[];
  /** How the score was computed — see TrustBreakdown. */
  breakdown: TrustBreakdown;
}

export const TRUST_DIMENSION_META: Record<TrustDimensionId, { name: string; sub: string }> = {
  conflicts: { name: 'Data conflicts', sub: 'Same figure cited with different values' },
  provenance: { name: 'Provenance', sub: 'Where each value came from' },
  citation: { name: 'Citation integrity', sub: 'Every claim resolves to a real source' },
  intent: { name: 'Intent ↔ evidence', sub: 'Project intent matches uploaded documents' },
};

export const TRUST_DIMENSION_ORDER: TrustDimensionId[] = ['conflicts', 'provenance', 'citation', 'intent'];

/**
 * Map an internal audit check-id to its plain-language trust dimension.
 * Anything that does not map to a named dimension is treated as a generic
 * data-conflict signal (the most conservative bucket) so nothing is dropped.
 */
export function trustDimensionForCheck(checkId: UserAuditFinding['checkId']): TrustDimensionId {
  switch (checkId) {
    // Provenance — where each value came from (doc provenance gates).
    case 'R-71':
      return 'provenance';
    // Citation integrity — every claim resolves to a real source.
    case 'R-3':
    case 'R-73':
    case 'R-15':
      return 'citation';
    // Intent ↔ evidence — project intent matches uploaded documents.
    case 'R-65':
      return 'intent';
    // Data conflicts — internal-number inconsistency + provider precedence +
    // canonical-fact disagreement, plus everything else (conservative default).
    case 'R-67':
    case 'tier-2e':
    case 'R-78':
    case 'R-79':
    default:
      return 'conflicts';
  }
}

/* ── Penalty weights ─────────────────────────────────────────────────── */

/**
 * Base penalty by severity — reproduces the historic inline weights
 * (block 14 / warn 5 / info 1) EXACTLY. Do not change without a
 * before/after impact pass: this directly moves the 0–100 trust score.
 */
export const BASE_PENALTY_BY_SEVERITY: Readonly<Record<AuditSeverity, number>> = {
  block: 14,
  warn: 5,
  info: 1,
};

/**
 * Per-tier penalty multiplier. ALL DEFAULT TO 1.0 in this slice so the
 * score is unchanged. A later tuning step may dial up e.g. Tier 0 to
 * weigh foundational checks more heavily — gated behind its own impact
 * report.
 */
export const MULT_BY_TIER: Readonly<Record<AuditTier, number>> = {
  'Tier 0': 1.0,
  'Tier 1': 1.0,
  'Tier 2': 1.0,
  'Tier 3': 1.0,
  'Tier 4': 1.0,
};

/** Neutral criticality weight — used whenever no override entry exists. */
export const DEFAULT_CRITICALITY = 1.0 as const;

/**
 * Per-canonical-fact criticality weighting. INTENTIONALLY EMPTY in this
 * slice — every fact resolves to `DEFAULT_CRITICALITY` (1.0). Populate
 * (e.g. `capacityMW: 1.5`) in a gated tuning step to make conflicts on
 * verdict-critical facts cost more trust.
 */
export const CRITICALITY_BY_FACT: Partial<Record<CanonicalFactKey, number>> = {};

/**
 * Per-check criticality weighting. INTENTIONALLY EMPTY in this slice —
 * every check resolves to `DEFAULT_CRITICALITY` (1.0).
 */
export const CRITICALITY_BY_CHECK: Partial<Record<AuditCheckId, number>> = {};

/**
 * Resolve the criticality multiplier for one finding. Prefers the
 * canonical-fact weight (when the finding carries `canonicalFactKey`),
 * then the per-check weight, then `DEFAULT_CRITICALITY`. With both
 * tables empty this always returns `1.0` ⇒ no score movement.
 */
export function criticalityFor(finding: UserAuditFinding): number {
  if (finding.canonicalFactKey != null) {
    const byFact = CRITICALITY_BY_FACT[finding.canonicalFactKey];
    if (byFact != null) return byFact;
  }
  const byCheck = CRITICALITY_BY_CHECK[finding.checkId];
  if (byCheck != null) return byCheck;
  return DEFAULT_CRITICALITY;
}

/**
 * Penalty contribution of a single finding:
 *   base[severity] × mult[tier] × criticality(finding).
 * With all multipliers at their 1.0 defaults this equals
 * `BASE_PENALTY_BY_SEVERITY[severity]` — identical to the prior inline
 * `f.severity === 'block' ? 14 : f.severity === 'warn' ? 5 : 1`.
 */
export function penaltyForFinding(finding: UserAuditFinding): number {
  const base = BASE_PENALTY_BY_SEVERITY[finding.severity] ?? 0;
  const tierMult = MULT_BY_TIER[finding.tier] ?? 1.0;
  return base * tierMult * criticalityFor(finding);
}

/**
 * Derive the four trust dimensions + an overall 0–100 trust score from the
 * audit findings. Blocking findings weigh more than warnings; the score is a
 * floored, monotonic readout — more / worse findings → lower trust.
 *
 * Pure: no React, no store, no I/O. Score = `clamp(round(100 - Σpenalty))`.
 */
export function deriveTrust(findings: UserAuditFinding[]): TrustResult {
  const byDim = new Map<TrustDimensionId, UserAuditFinding[]>();
  for (const id of TRUST_DIMENSION_ORDER) byDim.set(id, []);
  // Validated findings are settled user decisions — they stay in the result
  // for visibility but must not depress the trust score (score parity with
  // the era when they were filtered out of the result entirely).
  for (const f of findings) {
    if (f.validated || f.dismissed) continue;
    byDim.get(trustDimensionForCheck(f.checkId))!.push(f);
  }

  let penalty = 0;
  const lines: TrustPenaltyLine[] = [];
  const dimensions: TrustDimensionSummary[] = TRUST_DIMENSION_ORDER.map((id) => {
    const dimFindings = byDim.get(id)!;
    // Audit trail: one line per (dimension, severity) bucket that actually
    // charges points, so the score reads as arithmetic the user can check.
    for (const severity of ['block', 'warn', 'info'] as const) {
      const bucket = dimFindings.filter((f) => f.severity === severity);
      if (bucket.length === 0) continue;
      const points = bucket.reduce((sum, f) => sum + penaltyForFinding(f), 0);
      lines.push({
        dimensionId: id,
        dimensionName: TRUST_DIMENSION_META[id].name,
        severity,
        count: bucket.length,
        each: bucket.length > 0 ? points / bucket.length : 0,
        points,
      });
    }
    const hasBlock = dimFindings.some((f) => f.severity === 'block');
    const hasWarn = dimFindings.some((f) => f.severity === 'warn');
    for (const f of dimFindings) {
      penalty += penaltyForFinding(f);
    }
    const tone: TrustDimensionSummary['tone'] = hasBlock ? 'block' : hasWarn ? 'warn' : 'clean';
    // The number must mean exactly what its label says. "10 BLOCKING" when
    // the ledger said "4 blocking" was two lies at once: info rows inflated
    // the count AND warns were labelled blocking. A block-toned card counts
    // ONLY blocking findings; a warn-toned card counts only warns.
    const blockCount = dimFindings.filter((f) => f.severity === 'block').length;
    const warnCount = dimFindings.filter((f) => f.severity === 'warn').length;
    const count = hasBlock ? blockCount : warnCount;
    const note = hasBlock
      ? 'BLOCKING'
      : warnCount > 0
        ? 'TO REVIEW'
        : id === 'intent'
          ? 'ALIGNED'
          : 'CLEAR';
    const meta = TRUST_DIMENSION_META[id];
    return { id, name: meta.name, sub: meta.sub, tone, count, note };
  });

  const score = Math.max(0, Math.min(100, Math.round(100 - penalty)));

  const SEVERITY_RANK: Record<AuditSeverity, number> = { block: 0, warn: 1, info: 2 };
  lines.sort((a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity] || b.points - a.points);

  const byDimPoints = new Map<TrustDimensionId, number>();
  for (const l of lines) byDimPoints.set(l.dimensionId, (byDimPoints.get(l.dimensionId) ?? 0) + l.points);
  let worstDimension: TrustBreakdown['worstDimension'] = null;
  for (const [id, points] of byDimPoints) {
    if (points > 0 && (!worstDimension || points > worstDimension.points)) {
      worstDimension = { id, name: TRUST_DIMENSION_META[id].name, points };
    }
  }

  return {
    score,
    dimensions,
    breakdown: {
      startingScore: 100,
      totalPenalty: penalty,
      lines,
      excluded: {
        validated: findings.filter((f) => f.validated && !f.dismissed).length,
        dismissed: findings.filter((f) => f.dismissed).length,
      },
      weights: BASE_PENALTY_BY_SEVERITY,
      worstDimension,
    },
  };
}

/**
 * Plain-language one-liner naming what is actually holding the score back
 * (2026-08-14). The console previously hardcoded "held back by a blocking
 * DATA CONFLICT" no matter which dimension carried the block — on Triple
 * Threat it read that while Data conflicts showed 0 CLEAR and the block sat
 * in Provenance. Name the real dimension, or say the score is clean.
 */
export function trustHeadline(result: TrustResult): string {
  const { breakdown, score } = result;
  const blocking = breakdown.lines.filter((l) => l.severity === 'block');
  if (blocking.length > 0) {
    const names = [...new Set(blocking.map((l) => l.dimensionName.toLowerCase()))];
    const total = blocking.reduce((n, l) => n + l.count, 0);
    const where = names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
    return `Trust held back by ${total === 1 ? 'a blocking finding' : `${total} blocking findings`} in ${where} — resolve ${total === 1 ? 'it' : 'them'} before this site's reports are dispatch-ready.`;
  }
  const warns = breakdown.lines.filter((l) => l.severity === 'warn');
  if (warns.length > 0) {
    const total = warns.reduce((n, l) => n + l.count, 0);
    const where = [...new Set(warns.map((l) => l.dimensionName.toLowerCase()))].join(', ');
    return `No blocking conflicts. ${total} warning${total === 1 ? '' : 's'} in ${where} cost ${Math.round(warns.reduce((n, l) => n + l.points, 0))} points.`;
  }
  if (breakdown.totalPenalty > 0) {
    return `No blocking or warning findings — ${Math.round(breakdown.totalPenalty)} point${breakdown.totalPenalty === 1 ? '' : 's'} from informational rows only.`;
  }
  return score === 100
    ? 'Every trust dimension is clear — this site’s numbers are dispatch-ready.'
    : 'No outstanding findings.';
}
